import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import DocumentUpload from '../../common/DocumentUpload';
import { FormService } from '../../../services/patientmr/forms.service';
import type { FormRecordDto, FormDocumentDto } from '../../../dtos/patientmr/forms.dto';
import { useAuth } from '../../../context/authcontext';
import { usePatient } from '../../../context/patientContext';
import { fetchDemographics } from '../../../services/patientmr/demographics.service';
import { getAgencyByTenantId, formatAgencyZip } from '../../../services/admin/agency.service';
import SubmitterCertificationSection from './SubmitterCertificationSection';
import {
  F00012_SECTION_F_TITLE,
  F00012_SECTION_F_CERTIFICATIONS,
  F00012_SECTION_F_NOTE,
  f00012SectionFPrograms,
  F00012_HCPCS_COLUMNS,
  F00012_HCPCS_NOTE,
  type F00012HcpcsKey,
} from '../../../constants/tmhpFormText';
import PrintedNameField from './PrintedNameField';
import { therapyDisciplines } from '../../../constants/disciplines';

interface CCPPARequestFormProps {
  isReadOnly?: boolean;
  initialData?: Record<string, any>;
  onDataChange?: (data: any) => void;
  patientId?: string;
  formRecord?: FormRecordDto;
  onFormRecordChange?: (record: FormRecordDto) => void;
}

/** One Section E row; `modifier` and `frequency` are absent on rows saved before R-207. */
type HCPCSRow = Record<F00012HcpcsKey, string>;

const emptyHCPCSRow = (): HCPCSRow => ({ code: '', modifier: '', description: '', quantity: '', frequency: '', retailPrice: '' });

const CollapsibleSection: React.FC<{
  title: string; sectionKey: string;
  collapsed: boolean; onToggle: () => void;
  children: React.ReactNode; colorClass?: string;
}> = ({ title, collapsed, onToggle, children, colorClass = 'border-slate-200 bg-slate-50' }) => (
  <div className={`rounded-lg border ${colorClass} shadow-sm mb-4`}>
    <div className="flex items-center justify-between cursor-pointer px-4 py-3 hover:bg-slate-100 rounded-t-lg transition-colors" onClick={onToggle}>
      <h4 className="text-base font-semibold text-gray-900 text-left">{title}</h4>
      {collapsed ? <ChevronDown size={16} className="text-gray-500 shrink-0" /> : <ChevronUp size={16} className="text-gray-500 shrink-0" />}
    </div>
    <div className={`px-4 pb-4 pt-2 ${collapsed ? 'hidden' : 'block'}`}>{children}</div>
  </div>
);

const RequiredMark: React.FC = () => <span className="text-red-500 ml-0.5">*</span>;

const inputClass =
  'w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 text-sm text-left';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1 text-left';

const CCPPARequestForm: React.FC<CCPPARequestFormProps> = ({ isReadOnly = false, initialData = {}, onDataChange, patientId, formRecord, onFormRecordChange }) => {
  const { user } = useAuth();
  const { selectedPatient, serviceTracks } = usePatient();

  const [form, setForm] = useState<Record<string, any>>({
    requestTypeABA: false,
    requestTypeDME: false,
    requestTypeSupplies: false,
    requestTypePDN: false,
    requestTypePPECC: false,
    requestTypeInpatient: false,
    requestTypeOther: false,
    requestTypeOtherText: '',
    clientName: '',
    medicaidNumber: '',
    dateOfBirth: '',
    providerName: '',
    providerPhone: '',
    providerFax: '',
    providerAddress: '',
    providerCity: '',
    providerState: '',
    providerZip: '',
    taxId: '',
    npi: '',
    taxonomy: '',
    benefitCode: '',
    qrpName: '',
    qrpTaxId: '',
    qrpNpi: '',
    qrpTaxonomy: '',
    qrpBenefitCode: '',
    qrpAddress: '',
    qrpCity: '',
    qrpState: '',
    qrpZip: '',
    sectionCTypeABAEval: false,
    sectionCTypeABAReeVal: false,
    sectionCTypeABATreat: false,
    sectionCTypeInitial: false,
    sectionCTypeRecert: false,
    sectionCTypeRevision: false,
    sectionCABAEvalStart: '',
    sectionCABAEvalEnd: '',
    sectionCABAReevalStart: '',
    sectionCABAReevalEnd: '',
    sectionCABATreatStart: '',
    sectionCABATreatEnd: '',
    sectionCInitialStart: '',
    sectionCInitialEnd: '',
    sectionCRecertStart: '',
    sectionCRecertEnd: '',
    sectionCRevisionStart: '',
    sectionCRevisionEnd: '',
    sectionCRevisionReason: '',
    diagnosisMedicalNecessity: '',
    dosFrom: '',
    dosTo: '',
    hcpcsRows: [emptyHCPCSRow(), emptyHCPCSRow(), emptyHCPCSRow()] as HCPCSRow[],
    // Section F: official per-program certifications (cert1..cert3 were agency wording, retired)
    sectionFPrograms: [] as string[],
    sectionFCertified: {} as Record<string, boolean>,
    submitterAgreed: false,
    submitterName: '',
    submitterTitle: '',
    submitterDate: '',
    sigPhysician: '',
    sigDate: '',
    physicianName: '',
    physicianNpi: '',
    licenseNo: '',
    ...initialData,
  });

  useEffect(() => {
    if (formRecord !== undefined || !patientId) return;

    const autoPopulate = async () => {
      try {
        const activeTrack = (serviceTracks || []).find(t => t.phase !== 'DISCHARGED');
        const discipline = activeTrack?.discipline;
        const isPDN = discipline === 'PDN';
        const isTherapy = therapyDisciplines().includes(discipline || '');

        const [demographics, agency] = await Promise.all([
          fetchDemographics(patientId).catch(() => null),
          user?.tenantId ? getAgencyByTenantId(user.tenantId).catch(() => null) : Promise.resolve(null),
        ]);

        const updates: Record<string, any> = {};

        if (isPDN) updates.requestTypePDN = true;
        if (isTherapy) updates.requestTypeOther = true;

        if (selectedPatient?.firstName && selectedPatient?.lastName) {
          updates.clientName = `${selectedPatient.lastName}, ${selectedPatient.firstName}`;
        } else if (selectedPatient?.lastName) {
          updates.clientName = selectedPatient.lastName;
        } else if (selectedPatient?.firstName) {
          updates.clientName = selectedPatient.firstName;
        }

        if (selectedPatient?.dob) updates.dateOfBirth = selectedPatient.dob;

        const medicaidId = demographics?.medicaidID || demographics?.medicaidId;
        if (medicaidId) updates.medicaidNumber = medicaidId;

        if (agency?.legalName) updates.providerName = agency.legalName;
        if (agency?.contactInfo?.officePhone) updates.providerPhone = agency.contactInfo.officePhone;
        if (agency?.contactInfo?.fax) updates.providerFax = agency.contactInfo.fax;
        if (agency?.address?.street) updates.providerAddress = agency.address.street;
        if (agency?.address?.city) updates.providerCity = agency.address.city;
        if (agency?.address?.state) updates.providerState = agency.address.state;
        if (agency?.address?.zipCode) updates.providerZip = formatAgencyZip(agency);
        if (agency?.taxId) updates.taxId = agency.taxId;
        if (agency?.npi) updates.npi = agency.npi;
        if (agency?.taxonomyCode) updates.taxonomy = agency.taxonomyCode;
        if (agency?.benefitCode) updates.benefitCode = agency.benefitCode;

        setForm(prev => ({ ...prev, ...updates }));
      } catch (err) {
        console.error('[CCPPARequestForm] Auto-populate failed:', err);
      }
    };

    autoPopulate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onDataChange?.(form);
  }, [form, onDataChange]);

  // Section F applies per requested program; store which certification(s) apply
  const sectionFPrograms = f00012SectionFPrograms(form);
  const sectionFKey = sectionFPrograms.join(',');
  useEffect(() => {
    if (isReadOnly) return;
    setForm(prev => {
      const stored = Array.isArray(prev.sectionFPrograms) ? (prev.sectionFPrograms as string[]).join(',') : '';
      return stored === sectionFKey ? prev : { ...prev, sectionFPrograms: sectionFKey ? sectionFKey.split(',') : [] };
    });
  }, [sectionFKey, isReadOnly]);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (k: string) => setCollapsedSections(p => ({ ...p, [k]: !p[k] }));

  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);


  const handleFilesSelected = (files: File[]) => {
    if (formRecord?.status !== 'COMPLETE') {
      setActionError('Document upload is only available when the form is in Complete status.');
      return;
    }
    setActionError(null);
    setStagedFiles(prev => [...prev, ...files]);
  };

  const handleRemoveStagedFile = (index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadStagedFiles = async () => {
    if (!stagedFiles.length || !patientId || !formRecord) return;
    setActionError(null);
    setUploading(true);
    try {
      let updatedForm = formRecord;
      for (const file of stagedFiles) {
        updatedForm = await FormService.addDocument(patientId, formRecord.formId, file);
      }
      setStagedFiles([]);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Upload failed:', err);
      setActionError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      const result = await FormService.getDocumentUrl(patientId, formRecord.formId, doc.id);
      window.open(result.url, '_blank');
    } catch (err) {
      console.error('Download failed:', err);
      setActionError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    }
  };

  const handleDeleteDocument = async (doc: FormDocumentDto) => {
    if (!patientId || !formRecord) return;
    setActionError(null);
    try {
      setDeletingDocId(doc.id);
      const updatedForm = await FormService.deleteDocument(patientId, formRecord.formId, doc.id);
      onFormRecordChange?.(updatedForm);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError(err instanceof Error ? err.message : 'Delete failed. Please try again.');
    } finally {
      setDeletingDocId(null);
    }
  };

  const set = (field: string, value: any) => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const setHCPCS = (index: number, field: keyof HCPCSRow, value: string) => {
    if (isReadOnly) return;
    setForm(prev => {
      const rows = [...(prev.hcpcsRows as HCPCSRow[])];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, hcpcsRows: rows };
    });
  };

  const addHCPCSRow = () => {
    if (isReadOnly) return;
    setForm(prev => ({ ...prev, hcpcsRows: [...(prev.hcpcsRows as HCPCSRow[]), emptyHCPCSRow()] }));
  };

  const TYPE_C_OPTIONS = [
    { field: 'sectionCTypeABAEval', label: 'ABA Evaluation', startField: 'sectionCABAEvalStart', endField: 'sectionCABAEvalEnd' },
    { field: 'sectionCTypeABAReeVal', label: 'ABA Re-evaluation', startField: 'sectionCABAReevalStart', endField: 'sectionCABAReevalEnd' },
    { field: 'sectionCTypeABATreat', label: 'ABA Treatment', startField: 'sectionCABATreatStart', endField: 'sectionCABATreatEnd' },
    { field: 'sectionCTypeInitial', label: 'Initial / New Client', startField: 'sectionCInitialStart', endField: 'sectionCInitialEnd' },
    { field: 'sectionCTypeRecert', label: 'Recertification', startField: 'sectionCRecertStart', endField: 'sectionCRecertEnd' },
    { field: 'sectionCTypeRevision', label: 'Revision**', startField: 'sectionCRevisionStart', endField: 'sectionCRevisionEnd', isRevision: true },
  ];

  return (
    <div className="space-y-6 text-left">
      {/* Form Title */}
      <div className="mb-4">
        <p className="text-xs text-gray-500 uppercase tracking-wide text-left">Form Code: F00012</p>
        <p className="text-sm text-gray-600 text-left">CCP Prior Authorization Request Form (Comprehensive Care Program)</p>
      </div>

      <SubmitterCertificationSection formCode="F00012" data={form} onChange={set} isReadOnly={isReadOnly} />

      {/* Request Type */}
      <CollapsibleSection title="Request Type (Check all that apply)" sectionKey="request-type" collapsed={!!collapsedSections['request-type']} onToggle={() => toggleSection('request-type')} colorClass="border-slate-200 bg-slate-50">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { field: 'requestTypeABA', label: 'ABA' },
            { field: 'requestTypeDME', label: 'DME' },
            { field: 'requestTypeSupplies', label: 'Supplies' },
            { field: 'requestTypePDN', label: 'Private Duty Nursing' },
            { field: 'requestTypePPECC', label: 'PPECC' },
            { field: 'requestTypeInpatient', label: 'Inpatient Rehabilitation' },
            { field: 'requestTypeOther', label: 'Other' },
          ].map(({ field, label }) => (
            <label key={field} className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={!!form[field]}
                onChange={e => set(field, e.target.checked)}
                disabled={isReadOnly}
                className="rounded border-gray-300"
              />
              {label}
            </label>
          ))}
        </div>
        {form.requestTypeOther && (
          <div className="mt-2">
            <label className={labelClass}>Other (specify)</label>
            <input
              type="text"
              value={form.requestTypeOtherText}
              onChange={e => set('requestTypeOtherText', e.target.value)}
              readOnly={isReadOnly}
              className={inputClass}
            />
          </div>
        )}
      </CollapsibleSection>

      {/* Section A */}
      <CollapsibleSection title="Section A: Client Information" sectionKey="client-info" collapsed={!!collapsedSections['client-info']} onToggle={() => toggleSection('client-info')} colorClass="border-blue-200 bg-blue-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className={labelClass}>Client Name (Last, First, M.I.)<RequiredMark /></label>
            <input type="text" value={form.clientName} onChange={e => set('clientName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Medicaid Number<RequiredMark /></label>
            <input type="text" value={form.medicaidNumber} onChange={e => set('medicaidNumber', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Date of Birth<RequiredMark /></label>
            <input type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section B */}
      <CollapsibleSection title="Section B: Rendering Provider/Supplier Information" sectionKey="provider-info" collapsed={!!collapsedSections['provider-info']} onToggle={() => toggleSection('provider-info')} colorClass="border-indigo-200 bg-indigo-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Provider Name<RequiredMark /></label>
            <input type="text" value={form.providerName} onChange={e => set('providerName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Telephone</label>
            <input type="tel" value={form.providerPhone} onChange={e => set('providerPhone', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Fax</label>
            <input type="tel" value={form.providerFax} onChange={e => set('providerFax', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Street Address<RequiredMark /></label>
            <input type="text" value={form.providerAddress} onChange={e => set('providerAddress', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>City<RequiredMark /></label>
            <input type="text" value={form.providerCity} onChange={e => set('providerCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelClass}>State<RequiredMark /></label>
              <input type="text" maxLength={2} value={form.providerState} onChange={e => set('providerState', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>ZIP+4<RequiredMark /></label>
              <input type="text" value={form.providerZip} onChange={e => set('providerZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          </div>
          <div>
            <label className={labelClass}>Tax ID<RequiredMark /></label>
            <input type="text" value={form.taxId} onChange={e => set('taxId', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI<RequiredMark /></label>
            <input type="text" value={form.npi} onChange={e => set('npi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Taxonomy<RequiredMark /></label>
            <input type="text" value={form.taxonomy} onChange={e => set('taxonomy', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Benefit Code<RequiredMark /></label>
            <input type="text" value={form.benefitCode} onChange={e => set('benefitCode', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="mt-4">
          <p className="text-sm font-medium text-gray-700 mb-2 text-left">QRP (Qualified Rendering Provider) Information</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>QRP Name</label>
              <input type="text" value={form.qrpName} onChange={e => set('qrpName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP Tax ID</label>
              <input type="text" value={form.qrpTaxId} onChange={e => set('qrpTaxId', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP NPI</label>
              <input type="text" value={form.qrpNpi} onChange={e => set('qrpNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP Taxonomy</label>
              <input type="text" value={form.qrpTaxonomy} onChange={e => set('qrpTaxonomy', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP Benefit Code</label>
              <input type="text" value={form.qrpBenefitCode} onChange={e => set('qrpBenefitCode', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP Street Address</label>
              <input type="text" value={form.qrpAddress} onChange={e => set('qrpAddress', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>QRP City</label>
              <input type="text" value={form.qrpCity} onChange={e => set('qrpCity', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelClass}>QRP State</label>
                <input type="text" maxLength={2} value={form.qrpState} onChange={e => set('qrpState', e.target.value)} readOnly={isReadOnly} className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>QRP ZIP+4</label>
                <input type="text" value={form.qrpZip} onChange={e => set('qrpZip', e.target.value)} readOnly={isReadOnly} className={inputClass} />
              </div>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Section C */}
      <CollapsibleSection title="Section C: Type of Request" sectionKey="request-type-c" collapsed={!!collapsedSections['request-type-c']} onToggle={() => toggleSection('request-type-c')} colorClass="border-violet-200 bg-violet-50">
        <div className="space-y-4">
          {TYPE_C_OPTIONS.map(opt => (
            <div key={opt.field}>
              <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                <input
                  type="checkbox"
                  checked={!!form[opt.field]}
                  onChange={e => set(opt.field, e.target.checked)}
                  disabled={isReadOnly}
                  className="rounded border-gray-300"
                />
                {opt.label}
              </label>
              {form[opt.field] && (
                <div className="ml-6 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Requested Start Date<RequiredMark /></label>
                      <input type="date" value={form[opt.startField] || ''} onChange={e => set(opt.startField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Requested End Date<RequiredMark /></label>
                      <input type="date" value={form[opt.endField] || ''} onChange={e => set(opt.endField, e.target.value)} readOnly={isReadOnly} className={inputClass} />
                      {opt.isRevision && (
                        <p className="mt-1 text-xs text-gray-500 italic">(Cannot extend beyond current authorization period.)</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          {form.sectionCTypeRevision && (
            <div className="mt-4 pt-4 border-t border-violet-200">
              <label className={labelClass}>** Reason for Revision</label>
              <textarea rows={3} value={form.sectionCRevisionReason} onChange={e => set('sectionCRevisionReason', e.target.value)} readOnly={isReadOnly} className={inputClass} />
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Section D */}
      <CollapsibleSection title="Section D: Diagnosis and Medical Necessity" sectionKey="diagnosis" collapsed={!!collapsedSections['diagnosis']} onToggle={() => toggleSection('diagnosis')} colorClass="border-amber-200 bg-amber-50">
        <label className={labelClass}>Diagnosis and Medical Necessity<RequiredMark /></label>
        <textarea
          rows={5}
          value={form.diagnosisMedicalNecessity}
          onChange={e => set('diagnosisMedicalNecessity', e.target.value)}
          readOnly={isReadOnly}
          className={inputClass}
          placeholder="Provide diagnosis codes, clinical findings, and medical necessity justification..."
        />
      </CollapsibleSection>

      {/* Section E */}
      <CollapsibleSection title="Section E: Dates of Service and HCPCS Code" sectionKey="hcpcs" collapsed={!!collapsedSections['hcpcs']} onToggle={() => toggleSection('hcpcs')} colorClass="border-green-200 bg-green-50">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className={labelClass}>Dates of Service — From<RequiredMark /></label>
            <input type="date" value={form.dosFrom} onChange={e => set('dosFrom', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Dates of Service — To<RequiredMark /></label>
            <input type="date" value={form.dosTo} onChange={e => set('dosTo', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200 rounded-md">
            <thead>
              <tr className="bg-slate-50 text-left">
                {F00012_HCPCS_COLUMNS.map(col => (
                  <th key={col.key} className="px-3 py-2 font-medium text-gray-700 border-b border-slate-200">{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(form.hcpcsRows as HCPCSRow[]).map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100">
                  {F00012_HCPCS_COLUMNS.map(col => (
                    <td key={col.key} className="px-2 py-1">
                      <input
                        type="text"
                        aria-label={`${col.label} row ${idx + 1}`}
                        value={row[col.key] ?? ''}
                        onChange={e => setHCPCS(idx, col.key, e.target.value)}
                        readOnly={isReadOnly}
                        className={inputClass}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-1 text-xs text-gray-500 text-left">{F00012_HCPCS_NOTE}</p>
        {!isReadOnly && (
          <button
            type="button"
            onClick={addHCPCSRow}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            + Add Row
          </button>
        )}
      </CollapsibleSection>

      {/* Section F */}
      <CollapsibleSection title="Section F: Primary Practitioner's Certifications" sectionKey="certifications" collapsed={!!collapsedSections['certifications']} onToggle={() => toggleSection('certifications')} colorClass="border-red-200 bg-red-50">
        <p className="text-sm font-medium text-gray-800 mb-3 text-left">{F00012_SECTION_F_TITLE}</p>
        <div className="space-y-4 mb-4" data-testid="section-f-certifications">
          {sectionFPrograms.length === 0 && (
            <p className="text-sm text-gray-500 text-left">
              Check ABA, DME, Supplies, Private Duty Nursing or PPECC under Request Type to show the certification that applies.
            </p>
          )}
          {sectionFPrograms.map(program => {
            const cert = F00012_SECTION_F_CERTIFICATIONS[program];
            return (
              <div key={program}>
                <label className="flex items-start gap-2 text-sm font-semibold text-gray-800">
                  <input
                    type="checkbox"
                    aria-label={cert.heading}
                    checked={form.sectionFCertified?.[program] === true}
                    onChange={e => set('sectionFCertified', { ...(form.sectionFCertified || {}), [program]: e.target.checked })}
                    disabled={isReadOnly}
                    className="mt-0.5 rounded border-gray-300"
                  />
                  <span>{cert.heading}</span>
                </label>
                <ul className="ml-8 mt-1 list-disc text-sm text-gray-700 text-left">
                  {cert.statements.map(statement => <li key={statement}>{statement}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-600 mb-4 text-left">{F00012_SECTION_F_NOTE}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <PrintedNameField signer="Requesting physician or allowed practitioner" value={form.sigPhysician} onChange={v => set('sigPhysician', v)} readOnly={isReadOnly} inputClassName={inputClass} labelClassName={labelClass} />
          </div>
          <div>
            <label className={labelClass}>Date</label>
            <input type="date" value={form.sigDate} onChange={e => set('sigDate', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Printed or typed name of physician or allowed practitioner<RequiredMark /></label>
            <input type="text" value={form.physicianName} onChange={e => set('physicianName', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>NPI<RequiredMark /></label>
            <input type="text" value={form.physicianNpi} onChange={e => set('physicianNpi', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>License No.</label>
            <input type="text" value={form.licenseNo} onChange={e => set('licenseNo', e.target.value)} readOnly={isReadOnly} className={inputClass} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Generate PDF moved to page action bar */}

      {actionError && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex justify-between items-start">
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="ml-2 text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Documents */}
      {formRecord && (
        <>
          <DocumentUpload
            documents={(formRecord.documents ?? []).map(d => ({
              id: d.id,
              name: d.name,
              size: d.size,
              uploadedAt: d.uploadedAt,
            }))}
            stagedFiles={stagedFiles}
            canUpload={formRecord.status === 'COMPLETE'}
            onFilesSelected={handleFilesSelected}
            onStagedRemove={handleRemoveStagedFile}
            onDownload={handleDownloadDocument}
            onDelete={handleDeleteDocument}
            uploading={uploading}
            deletingId={deletingDocId}
            collapsible={true}
            defaultOpen={true}
            maxSizeMb={7}
          />
          {stagedFiles.length > 0 && formRecord.status === 'COMPLETE' && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleUploadStagedFiles}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? 'Uploading...' : 'Upload Files'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CCPPARequestForm;
